import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import { useTrialDashboardMobileTypographyStyles } from '../../shared/useTrialDashboardMobileTypographyStyles'
import { useStatCardSharedStyles } from '../shared/useStatCardSharedStyles'

const useLayoutStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    position: 'relative',
    overflowX: 'visible',
    width: '100%',
    minHeight: 'calc(65vh - 200px)',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: 'transparent',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 0),
      gap: theme.spacing(2),
      minHeight: 'calc(100vh - 150px)'
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 0),
      gap: theme.spacing(3)
    }
  },
  whiteBackground: {
    width: '100vw',
    maxWidth: '100vw',
    height: 'auto',
    minHeight: '202px',
    position: 'absolute',
    top: '50%',
    left: 'calc(-50vw + 50%)',
    transform: 'translateY(-50%)',
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    marginBottom: 0,
    order: 2,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0,
    border: '1.19px solid #D9DEE8',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    boxSizing: 'border-box',
    overflow: 'visible',
    zIndex: 1,
    '@media (max-width: 599px)': {
      minHeight: '520px',
      width: '100vw',
      maxWidth: '100vw',
      padding: 0,
      overflow: 'visible',
      position: 'relative',
      top: 'auto',
      left: 0,
      transform: 'none',
      marginTop: theme.spacing(2),
      marginLeft: `calc((100vw - 100%) / -2)`,
      marginRight: `calc((100vw - 100%) / -2)`
    },
    [theme.breakpoints.down('xs')]: {
      minHeight: '480px',
      width: '100vw',
      maxWidth: '100vw',
      padding: 0,
      overflow: 'visible',
      position: 'relative',
      top: 'auto',
      left: 0,
      transform: 'none',
      marginTop: theme.spacing(1.5),
      marginLeft: `calc((100vw - 100%) / -2)`,
      marginRight: `calc((100vw - 100%) / -2)`
    },
    '@media (min-width: 600px) and (max-width: 789px)': {
      minHeight: '350px'
    },
    '@media (min-width: 768px) and (max-width: 1024px)': {
      minHeight: '380px',
      top: 'calc(50% + 320px)'
    },
    [theme.breakpoints.up('sm')]: {
      width: '100vw',
      maxWidth: '100vw',
      padding: 0,
      top: 'calc(50% + 300px)',
      left: 'calc(-50vw + 50%)',
      transform: 'translateY(-50%)'
    },
    [theme.breakpoints.up('md')]: {
      minHeight: '380px',
      width: '100vw',
      maxWidth: '100vw',
      padding: 0,
      top: 'calc(50% + 340px)',
      left: 'calc(-50vw + 50%)',
      transform: 'translateY(-50%)'
    },
    '@media (min-width: 1024px) and (max-height: 700px)': {
      top: 'calc(50% + 360px)',
      left: 'calc(-50vw + 50%)',
      transform: 'translateY(-50%)'
    }
  },
  leftSection: {
    padding: theme.spacing(2, 1),
    width: '100%',
    order: 1,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 0.5)
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 1)
    }
  },
  rightSection: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
    top: 0,
    left: 0,
    transform: 'none',
    order: 0,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto'
    },
    '@media (max-width: 599px)': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    }
  },
  statsGrid: {
    marginBottom: '60px',
    display: 'grid',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: 600,
    margin: '0 auto',
    padding: 0,
    gridTemplateColumns: 'repeat(2, 1fr)',
    '@media (max-width: 599px)': {
      gridTemplateColumns: '1fr',
      padding: 0,
      justifyItems: 'center',
      width: 'auto',
      maxWidth: '100%',
      margin: '0 auto'
    },
    '@media (min-width: 600px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      padding: 0,
      margin: '0 auto'
    }
  },
  statCard: {
    width: '293.07px',
    height: '204.834px',
    padding: 0,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    zIndex: 2,
    flexShrink: 0,
    '@media (max-width: 599px)': {
      width: '206.829px',
      height: '121.143px',
      flexShrink: 0,
      minHeight: '121.143px',
      maxHeight: '121.143px',
      zIndex: 2,
      overflow: 'visible'
    }
  }
}))

export const useTrialOverDashboardMobileLayoutStyles = () => {
  const layoutStyles = useLayoutStyles()
  const typographyStyles = useTrialDashboardMobileTypographyStyles()
  const sharedStyles = useStatCardSharedStyles()
  return { ...layoutStyles, ...typographyStyles, ...sharedStyles }
}
